import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { opsAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, User, Building2, CreditCard, Clock, Shield,
  Flame, Loader2, Crown, Play, Pause, Trash2, Eye, Plus,
  Phone, ShoppingBag, LogOut, AlertTriangle
} from "lucide-react";

export default function OpsUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Comp dialog
  const [compOpen, setCompOpen] = useState(false);
  const [compDuration, setCompDuration] = useState("1_month");
  const [compPlan, setCompPlan] = useState("premium");
  const [compReason, setCompReason] = useState("");
  const [compLoading, setCompLoading] = useState(false);

  // Billing edit
  const [billingPlan, setBillingPlan] = useState("");
  const [billingStatus, setBillingStatus] = useState("");

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await opsAPI.getUser(userId);
      setData(res.data);
      setBillingPlan(res.data.billing?.plan || "");
      setBillingStatus(res.data.billing?.status || "");
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) navigate("/ops");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUser(); }, [userId]);

  const handleSuspend = async () => {
    try {
      await opsAPI.suspendUser(userId);
      toast.success("User suspended");
      fetchUser();
    } catch (err) { toast.error("Failed to suspend"); }
  };

  const handleActivate = async () => {
    try {
      await opsAPI.activateUser(userId);
      toast.success("User activated");
      fetchUser();
    } catch (err) { toast.error("Failed to activate"); }
  };

  const handleDelete = async () => {
    try {
      await opsAPI.deleteUser(userId);
      toast.success("User deleted");
      navigate("/ops");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleImpersonate = async () => {
    try {
      const res = await opsAPI.impersonate(userId);
      localStorage.setItem("admin_token", localStorage.getItem("token"));
      localStorage.setItem("token", res.data.token);
      toast.success(`Impersonating ${res.data.user.name}`);
      window.location.href = "/dashboard";
    } catch (err) { toast.error("Failed to impersonate"); }
  };

  const handleUpdateBilling = async () => {
    try {
      await opsAPI.updateBilling(userId, { plan: billingPlan, status: billingStatus });
      toast.success("Billing updated");
      fetchUser();
    } catch (err) { toast.error("Failed to update billing"); }
  };

  const handleGrantComp = async () => {
    setCompLoading(true);
    try {
      const res = await opsAPI.grantComp(userId, { duration: compDuration, plan: compPlan, reason: compReason });
      toast.success(res.data.message);
      setCompOpen(false);
      fetchUser();
    } catch (err) { toast.error("Failed to grant comp"); }
    finally { setCompLoading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!data) return null;

  const { user, restaurant, billing, activity, usage } = data;
  const status = user.status || "active";

  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="ops-user-detail">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/zest-logo.png" alt="Zest" className="h-8 w-auto" />
            <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[10px] ml-1">
              <Shield className="w-2.5 h-2.5 mr-1" /> OPS
            </Badge>
          </div>
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs h-8" onClick={() => navigate("/ops")}>
            <ArrowLeft className="w-3 h-3 mr-1" /> All Users
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* User Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-xl font-bold">
              {user.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{user.name}</h1>
              <p className="text-sm text-slate-500">{user.email}</p>
              <div className="flex gap-2 mt-1">
                <Badge variant="outline" className={`badge-pill text-[10px] ${user.role === "super_admin" ? "bg-red-50 text-red-600 border-red-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{user.role}</Badge>
                <Badge variant="outline" className={`badge-pill text-[10px] ${status === "active" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`}>{status}</Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {restaurant && (
              <Button size="sm" className="h-9 text-xs bg-orange-500 hover:bg-orange-600" onClick={() => navigate(`/ops/users/${userId}/restaurant`)} data-testid="manage-restaurant-button">
                <Building2 className="w-3.5 h-3.5 mr-1.5" /> Manage Restaurant
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={handleImpersonate} data-testid="impersonate-button">
              <Eye className="w-3.5 h-3.5 mr-1.5" /> Impersonate
            </Button>
            {status === "active" ? (
              <Button variant="outline" size="sm" className="h-9 text-xs text-amber-600 border-amber-200 hover:bg-amber-50" onClick={handleSuspend} data-testid="suspend-button">
                <Pause className="w-3.5 h-3.5 mr-1.5" /> Suspend
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="h-9 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={handleActivate} data-testid="activate-button">
                <Play className="w-3.5 h-3.5 mr-1.5" /> Activate
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteOpen(true)} data-testid="delete-button">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: Restaurant + Usage */}
          <div className="space-y-5">
            <Card className="border-slate-100">
              <CardHeader className="pb-2 px-5 pt-5">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Building2 className="w-4 h-4 text-slate-400" /> Restaurant
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5 space-y-2">
                {restaurant ? (
                  <>
                    <p className="text-[13px] font-medium text-slate-800">{restaurant.business_name}</p>
                    <p className="text-[11px] text-slate-400">{restaurant.address}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge className={restaurant.ai_active ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-slate-100 text-slate-400"} variant="outline">
                        AI {restaurant.ai_active ? "Active" : "Inactive"}
                      </Badge>
                      <Badge className={restaurant.pos_connected ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-slate-100 text-slate-400"} variant="outline">
                        POS {restaurant.pos_connected ? restaurant.pos_type : "None"}
                      </Badge>
                    </div>
                    {restaurant.twilio_number && <p className="text-[12px] font-mono text-slate-500 mt-1">{restaurant.twilio_number}</p>}
                  </>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[12px] text-slate-400">No restaurant configured</p>
                    <Button size="sm" className="h-8 text-xs" onClick={async () => {
                      const name = prompt("Enter restaurant name:");
                      if (!name) return;
                      try {
                        await opsAPI.createRestaurant({ user_id: userId, business_name: name });
                        toast.success("Restaurant created!");
                        fetchUser();
                      } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                    }} data-testid="create-restaurant-button">
                      <Plus className="w-3 h-3 mr-1" /> Create Restaurant
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardHeader className="pb-2 px-5 pt-5">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Phone className="w-4 h-4 text-slate-400" /> Usage
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5 space-y-2">
                <div className="flex justify-between text-[13px]"><span className="text-slate-500">Total Calls</span><span className="font-medium text-slate-800">{usage?.total_calls || 0}</span></div>
                <div className="flex justify-between text-[13px]"><span className="text-slate-500">Total Orders</span><span className="font-medium text-slate-800">{usage?.total_orders || 0}</span></div>
              </CardContent>
            </Card>
          </div>

          {/* Middle: Billing */}
          <div className="space-y-5">
            <Card className="border-slate-100">
              <CardHeader className="pb-2 px-5 pt-5 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <CreditCard className="w-4 h-4 text-slate-400" /> Billing
                </CardTitle>
                <Button variant="outline" size="sm" className="h-7 text-[10px] text-orange-600 border-orange-200" onClick={() => setCompOpen(true)} data-testid="grant-comp-button">
                  <Crown className="w-3 h-3 mr-1" /> Grant Comp
                </Button>
              </CardHeader>
              <CardContent className="px-5 pb-5 space-y-4">
                {billing?.status === "comp" && (
                  <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider mb-1">Complimentary Access</p>
                    <p className="text-[12px] text-amber-600">Duration: {billing.comp_duration?.replace("_", " ")}</p>
                    {billing.comp_reason && <p className="text-[11px] text-amber-500 mt-1">Reason: {billing.comp_reason}</p>}
                    {billing.comp_expires_at && <p className="text-[11px] text-amber-500">Expires: {new Date(billing.comp_expires_at).toLocaleDateString()}</p>}
                  </div>
                )}
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px]">Plan</Label>
                    <Select value={billingPlan} onValueChange={setBillingPlan}>
                      <SelectTrigger className="h-9 text-sm" data-testid="billing-plan-select"><SelectValue placeholder="Select plan" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="starter">Starter ($199/mo)</SelectItem>
                        <SelectItem value="premium">Premium ($399/mo)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px]">Status</Label>
                    <Select value={billingStatus} onValueChange={setBillingStatus}>
                      <SelectTrigger className="h-9 text-sm" data-testid="billing-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="past_due">Past Due</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="comp">Comp</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button size="sm" className="h-9 text-xs w-full" onClick={handleUpdateBilling} data-testid="save-billing">Save Billing</Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Activity Log */}
          <Card className="border-slate-100">
            <CardHeader className="pb-2 px-5 pt-5">
              <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Clock className="w-4 h-4 text-slate-400" /> Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              {activity?.length > 0 ? (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {activity.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                        a.action === "deleted" ? "bg-red-400" : a.action === "suspended" ? "bg-amber-400"
                        : a.action === "comp_granted" ? "bg-orange-400" : a.action === "impersonated" ? "bg-violet-400"
                        : "bg-blue-400"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-medium text-slate-700">{a.action?.replace(/_/g, " ")}</p>
                        <p className="text-[10px] text-slate-400">{a.admin_email} &middot; {new Date(a.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[12px] text-slate-400 text-center py-6">No activity logged</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Comp Access Dialog */}
      <Dialog open={compOpen} onOpenChange={setCompOpen}>
        <DialogContent data-testid="comp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Crown className="w-5 h-5 text-orange-500" /> Grant Complimentary Access
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Plan</Label>
              <Select value={compPlan} onValueChange={setCompPlan}>
                <SelectTrigger data-testid="comp-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Duration</Label>
              <Select value={compDuration} onValueChange={setCompDuration}>
                <SelectTrigger data-testid="comp-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1_month">1 Month</SelectItem>
                  <SelectItem value="3_months">3 Months</SelectItem>
                  <SelectItem value="6_months">6 Months</SelectItem>
                  <SelectItem value="1_year">1 Year</SelectItem>
                  <SelectItem value="forever">Forever</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Reason (optional)</Label>
              <Textarea placeholder="Beta tester, investor, partnership..." value={compReason} onChange={(e) => setCompReason(e.target.value)} rows={2} data-testid="comp-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCompOpen(false)}>Cancel</Button>
            <Button onClick={handleGrantComp} disabled={compLoading} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-comp">
              {compLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Crown className="w-3.5 h-3.5 mr-1.5" />}
              Grant Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle className="w-5 h-5" /> Delete User
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">This will permanently delete <strong>{user.name}</strong> ({user.email}) and all their data including restaurant, menu, calls, orders, and knowledge graph. This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">Delete Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
